import { useEffect } from 'react';

const MAX_ANGLE_DEG = 22;
const CYL_RADIUS_PX = 1200;

// One scroll listener, one rAF, one IntersectionObserver — shared across every card.
// Off-screen cards are skipped entirely. Reads are batched before writes to avoid
// layout thrashing (forced sync layout on every getBoundingClientRect).

const visible = new Set();
let raf = 0;
let started = false;
let io = null;
const reads = [];
let centeredEl = null;

const update = () => {
  raf = 0;
  const vh = window.innerHeight || 1;
  const half = vh / 2;
  // Phase 1: read all rects (no writes in between → no thrash).
  reads.length = 0;
  visible.forEach(el => {
    const rect = el.getBoundingClientRect();
    reads.push([el, rect.top + rect.height / 2]);
  });
  // Phase 2: write CSS variables AND find the card closest to viewport centre.
  let bestEl = null;
  let bestDist = Infinity;
  for (let i = 0; i < reads.length; i++) {
    const [el, center] = reads[i];
    const p = Math.max(-1, Math.min(1, (center - half) / half));
    const angleRad = p * (MAX_ANGLE_DEG * Math.PI) / 180;
    const z = -CYL_RADIUS_PX * (1 - Math.cos(angleRad));
    el.style.setProperty('--p', p.toFixed(3));
    el.style.setProperty('--cyl-z', z.toFixed(1) + 'px');
    const dist = Math.abs(p);
    if (dist < bestDist) {
      bestDist = dist;
      bestEl = el;
    }
  }
  // The .container we want to mark is the card-reveal's child, not the wrapper itself.
  const newCentered = bestEl ? bestEl.querySelector('.container') : null;
  if (newCentered !== centeredEl) {
    if (centeredEl) centeredEl.classList.remove('is-centered');
    if (newCentered) newCentered.classList.add('is-centered');
    centeredEl = newCentered;
  }
};

const requestUpdate = () => {
  if (raf) return;
  raf = requestAnimationFrame(update);
};

const ensureStarted = () => {
  if (started) return;
  started = true;
  io = new IntersectionObserver((entries) => {
    let anyChanged = false;
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        if (!visible.has(entry.target)) {
          visible.add(entry.target);
          anyChanged = true;
        }
      } else {
        if (visible.delete(entry.target)) anyChanged = true;
      }
    });
    if (anyChanged) requestUpdate();
  }, { rootMargin: '200px 0px' });
  window.addEventListener('scroll', requestUpdate, { passive: true });
  window.addEventListener('resize', requestUpdate);
};

export function useCardTilt(ref) {
  useEffect(() => {
    const el = ref && ref.current;
    if (!el) return;
    ensureStarted();
    io.observe(el);
    return () => {
      visible.delete(el);
      if (io) io.unobserve(el);
    };
  }, [ref]);
}
